import {
    Component,
    HostListener,
    OnDestroy,
    ViewChild,
    ElementRef,
    ChangeDetectorRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { map, Subject, takeUntil, tap } from 'rxjs';
import { PATHS } from 'src-ironside/app-routing.module';
import { ModifyFavoriteComponent } from 'src-ironside/my-favourites/modify-favorite/modify-favorite';
import { TranslateDynamicLanguagePipe } from 'src-ironside/pipes/translateDynamicLanguage.pipe';
import { CategoryRepository } from 'src-ironside/repositories/category.repository';
import { ComparatorRepository } from 'src-ironside/repositories/comparator.repository';
import { Favorite, FavoriteRepository } from 'src-ironside/repositories/favorite.repository';
import { Product, ProductRepository } from 'src-ironside/repositories/product.repository';
import { ResourceRepository } from 'src-ironside/repositories/resource.repository';
import { RouterService } from 'src-ironside/router.service';
import { CommonService } from 'src-ironside/services/common.service';
import { BreadCrumbItem } from 'src-ironside/shared/components/bread-crumb/bread-crumb.component';
import {
    buildCategoryHierarchyRandom,
    findCategoryLv1ByCategories,
} from 'src-ironside/utils/categories';
import { DynamicDocumentService } from 'src/app/shared/services/dynamic-document.service';

@Component({
    selector: 'app-new-product-detail',
    templateUrl: 'new-productDetail.component.html',
    styleUrls: ['./new-productDetail.component.css'],
    providers: [ProductRepository, ResourceRepository, CategoryRepository, MessageService],
})
export class NewProductDetailComponent implements OnDestroy {
    @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
    @ViewChild('galleriaRef') galleriaRef: ElementRef;

    id: string;
    listImage?: string[];
    selected: boolean;
    imgShow?: string;
    imgDefault = 'src-ironside/assets/images/image-default.jpg';

    currentProduct?: Product;
    favorites: Favorite[];
    destroyRef = new Subject();

    idProduct: string;
    responsiveOptions: any[];
    orientation: string = 'vertical';
    activeIndex = 0;

    private _breadCrumbs: BreadCrumbItem[] = [];

    extranetRestrictions: { champs: string[]; fonctions: string[]; menu: string[] } = {
        champs: [],
        fonctions: [],
        menu: [],
    };

    constructor(
        private routerService: RouterService,
        private activatedRoute: ActivatedRoute,
        private productRepository: ProductRepository,
        private comparatorRepository: ComparatorRepository,
        public favoriteRepository: FavoriteRepository,
        public resourceRepository: ResourceRepository,
        private dialog: MatDialog,
        private categoryRepository: CategoryRepository,
        private _messageService: MessageService,
        private translateDynamicLanguage: TranslateDynamicLanguagePipe,
        private translate: TranslateService,
        private _commonService: CommonService,
        private dynamicDocumentService: DynamicDocumentService,
        private cdr: ChangeDetectorRef
    ) {
        window.scrollTo(0, 0);
        this.activatedRoute.params.pipe(takeUntil(this.destroyRef)).subscribe((params) => {
            if (params['id']) {
                const filters = [
                    {
                        fieldName: 'referenceProduct',
                        conditions: [
                            {
                                type: 'EQUALS',
                                value: params['id'],
                            },
                        ],
                    },
                ];

                this.dynamicDocumentService
                    .find(
                        {
                            collectionName: 'dynamic_product',
                            filters,
                            sorts: {
                                publishedOrder: 'DESC',
                            },
                            page: 1,
                            pageSize: 1,
                        },
                        true
                    )
                    .pipe(
                        map((res) => ({
                            content: res?.content?.map((product: any) =>
                                this.productRepository.convertProductOriginalLanguage(product)
                            ),
                        }))
                    )
                    .subscribe((res) => {
                        const result = res?.content?.[0];
                        this.getProductDetail(result?.id);
                        this.idProduct = result?.id;

                        this.productRepository
                            .loadFullExtendDataByGroup(res?.content, false)
                            .subscribe({
                                next: (res) => {
                                    if (this.currentProduct) {
                                        // Giữ lại các fields từ parent product (ce, other, otherStandards)
                                        const parentFields = {
                                            ce: this.currentProduct.ce,
                                            other: this.currentProduct.other,
                                            otherStandards: this.currentProduct.otherStandards,
                                        };

                                        this.currentProduct = {
                                            ...res?.[0],
                                            ...parentFields, // Giữ lại fields từ parent
                                            resources: this.getImageValid(res?.[0]?.fullExtendData),
                                        };

                                        if (this.currentProduct) {
                                            this.currentProduct.isFavorite =
                                                this.favoriteRepository.isFavorited(
                                                    this.currentProduct.id || ''
                                                );
                                        }
                                    }
                                },
                                error: (error) => {
                                    console.error('Error loading product data:', error);
                                },
                            });
                    });
            }
        });
    }

    ngOnInit() {
        /*  this._breadCrumbs = [
            { name: this.translate.instant('Menu.home'), url: PATHS.home_en },
            { name: this.translate.instant('Menu.product'), isEnable: false },
        ]; */

        this.getFavorites();

        const windowWidth = window.innerWidth;
        if (windowWidth > 1280) {
            this.orientation = 'vertical';
        } else {
            this.orientation = 'horizontal';
        }

        this._commonService.getExtranetRestrictionsStore().subscribe((res) => {
            this.extranetRestrictions = res;
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
        const windowWidth = (event.target as Window).innerWidth;
        if (windowWidth > 1280) {
            this.orientation = 'vertical';
        } else {
            this.orientation = 'horizontal';
        }
    }

    getImageValid(value: any): any[] {
        if (!Array.isArray(value)) {
            this.imgShow = this.imgDefault;
            this.checkImageValid(this.imgShow as string);
            return [];
        }

        const result = value.flatMap(
            (el: any) =>
                el?.resources?.filter(
                    (item: any) =>
                        item?.files?.[0]?.thumbnailUrl && item?.typeOfResource === 'PHOTO'
                ) || []
        );

        // Set activeIndex về 0 và load ảnh đầu tiên
        this.activeIndex = 0;
        const firstImage = result?.[0]?.files?.[0]?.url || result?.[0]?.files?.[0]?.thumbnailUrl;
        this.imgShow = firstImage ?? this.imgDefault;
        this.checkImageValid(this.imgShow as string);

        return result;
    }

    get breadCrumb() {
        const defaultBreadcrumbs = [
            { name: this.translate.instant('Menu.home'), url: PATHS.home_en },
            { name: this.translate.instant('Menu.product'), isEnable: false },
        ];

        return [...defaultBreadcrumbs, ...this._breadCrumbs].map((item) => ({
            ...item,
            name: item.name,
            url: item?.url,
            isEnable: item?.isEnable,
        }));
    }

    getProductDetail(id: string) {
        this.productRepository
            .findDetailById(id)
            .pipe(takeUntil(this.destroyRef))
            .subscribe((product: Product) => {
                this.currentProduct = product;
                this.resourceRepository
                    .findByIds(product.resources || [])
                    .pipe(takeUntil(this.destroyRef))
                    .subscribe((res) => {
                        this.listImage = [];
                        res.forEach((resource) => {
                            for (let index = 0; index < resource.files?.length; index++) {
                                const file = resource.files[index];
                                if (!file.url?.endsWith('pdf')) {
                                    this.listImage?.push(file.url);
                                } else {
                                    this.listImage?.push(this.imgDefault);
                                }
                            }
                        });

                        this.imgShow = this.listImage?.[0] ?? this.imgDefault;
                        this.checkImageValid(this.imgShow);
                        // Set activeIndex về 0 và load ảnh đầu tiên
                        this.activeIndex = 0;
                        this.loadActiveImage();
                    });
                /* Get BreadCrumb */
                this.categoryRepository
                    .findAllCategoryLevel(3)
                    .pipe(takeUntil(this.destroyRef))
                    .subscribe({
                        next: (res) => {
                            const categories = buildCategoryHierarchyRandom(res);

                            const category: any[] = [];
                            const foundItem = res?.find((item) =>
                                item?.linkProduct?.some(
                                    (product) => product?.documentId == this.idProduct
                                )
                            );

                            const addCategoryToBreadcrumb = (item: any) => {
                                if (item) {
                                    const matchedCategory = this.findCategoryById(
                                        item.id,
                                        categories
                                    );

                                    category.push({
                                        name: this.translateDynamicLanguage.transform(
                                            item?.nameCategory
                                        ),
                                        level: item?.categoryLevelCategory,
                                        id: item?.id,
                                        categories: matchedCategory ? matchedCategory : null,
                                    });
                                }
                            };

                            if (foundItem) {
                                const findCategories = findCategoryLv1ByCategories(categories, [
                                    foundItem,
                                ]);

                                switch (foundItem.categoryLevelCategory) {
                                    case 1:
                                        this._breadCrumbs.push({
                                            name:
                                                this.translateDynamicLanguage.transform(
                                                    foundItem?.nameCategory
                                                ) || '',
                                            id: foundItem?.id,
                                            categories,
                                        });
                                        break;
                                    case 2:
                                        findCategories.forEach((item) => {
                                            addCategoryToBreadcrumb(item);
                                        });
                                        break;
                                    case 3:
                                        findCategories.forEach((item) => {
                                            addCategoryToBreadcrumb(item);
                                        });
                                        break;
                                }
                                category
                                    .sort((a, b) => a?.level! - b.level!)
                                    .forEach((el) => {
                                        this._breadCrumbs.push({
                                            name: el?.name || '',
                                            id: el?.id,
                                            categories: el?.categories ?? [],
                                        });
                                    });
                            }
                        },
                        error: () => {
                            this._breadCrumbs.push({
                                name:
                                    this.translateDynamicLanguage.transform(
                                        product.shortDescription
                                    ) || '',
                            });
                        },
                        complete: () => {
                            if (this.currentProduct) {
                                this.currentProduct.isFavorite =
                                    this.favoriteRepository.isFavorited(
                                        this.currentProduct.id || ''
                                    );
                            }
                            this._breadCrumbs.push({
                                name:
                                    this.translateDynamicLanguage.transform(
                                        product.shortDescription
                                    ) || '',
                            });
                        },
                    });
            });
    }

    removeFromFavorites() {
        if (!this.currentProduct?.id) return;

        this.currentProduct.isFavorite = false;
        this.favoriteRepository.unFavorite(this.currentProduct.id);
    }

    addToFavorites(favId: string) {
        if (!this.currentProduct?.id) return;
        this.currentProduct!.isFavorite = true;
        this.favoriteRepository.favorite(favId, this.currentProduct?.id!);
    }

    checkHasFavorites() {
        if (this.favoriteRepository.favorites?.length === 0) {
            this.trigger.closeMenu();

            const dialogRef = this.dialog.open(ModifyFavoriteComponent);
            dialogRef.componentInstance.onClose
                .asObservable()
                .pipe(
                    tap((res) => {
                        if (res) {
                            this.getFavorites();
                        }
                    })
                )
                .subscribe();
        }
    }

    showImage(img: string): void {
        this.imgShow = img;
        this.checkImageValid(img);
    }

    public toggleSelected() {
        this.selected = !this.selected;
    }

    getTranslatedRoute(route?: string) {
        return this.routerService.getTranslatedRoute(route);
    }

    goToFeatures() {
        const el = document.getElementById('features');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    }

    addProductToCompare(id?: string) {
        let listProductCompare = JSON.parse(localStorage.getItem('compare') as any);

        if (listProductCompare?.length > 4) {
            this._messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Maximum 5 produits',
                life: 3000,
            });
            return;
        }
        if (!id) return;

        this.comparatorRepository.addProduct([id]);
    }

    onToggleIcon(event: any, color: string) {
        const iconURL = `/src-ironside/assets/icons/comparator_${color}.svg`;

        const imgTag = (event.target as HTMLElement)?.querySelector('#comparator-icon');

        if (imgTag) {
            imgTag.setAttribute('src', iconURL);
        }
    }

    onImgError(event: Event) {
        const target = event.target as HTMLImageElement;
        target.src = this.imgDefault; // Đặt ảnh mặc định
    }

    checkImageValid(url: string) {
        const img = new Image();
        img.src = url;

        img.onerror = () => {
            this.imgShow = this.imgDefault;
        };
    }

    findCategoryById(id: string, categories: any): any {
        for (let i = 0; i < categories.length; i++) {
            if (categories[i].id === id) {
                return categories[i];
            }

            if (categories[i].subcategoryList && categories[i].subcategoryList.length > 0) {
                const result = this.findCategoryById(id, categories[i]?.subcategoryList ?? []);
                if (result) {
                    return result;
                }
            }
        }
        return null;
    }

    isPermissionFonctions(role: any) {
        return this.extranetRestrictions?.fonctions?.includes(role);
    }

    onOpenDialog(product: Product) {
        const dialogRef = this.dialog.open(ModifyFavoriteComponent);
        dialogRef.componentInstance.onClose
            .asObservable()
            .pipe(
                tap((res) => {
                    if (res) {
                        this.addToFavorites(res?.id);
                    }
                })
            )
            .subscribe(() => {
                this.getFavorites();
            });
    }

    getFavorites() {
        this.favoriteRepository
            .getMyFavoriteList()
            .pipe(takeUntil(this.destroyRef))
            .subscribe((res) => {
                this.favorites = res;
            });
    }

    onPageChange(event: any) {
        this.imgShow =
            this.currentProduct?.resources?.[event?.page]?.files?.[0]?.url ?? this.imgDefault;
        this.checkImageValid(this.imgShow as string);
    }

    ngOnDestroy(): void {
        this.destroyRef.next(null);
        this.destroyRef.complete();
    }

    /**
     * Chọn ảnh theo index và load ảnh lớn
     */
    selectImage(index: number): void {
        if (index >= 0 && index < (this.currentProduct?.resources?.length ?? 0)) {
            this.activeIndex = index;
            this.loadActiveImage();
        }
    }

    /**
     * Load ảnh lớn từ resource đang active
     */
    loadActiveImage(): void {
        const item = this.currentProduct?.resources?.[this.activeIndex];
        const url = item?.files?.[0]?.url || item?.files?.[0]?.thumbnailUrl;
        if (url) {
            this.showImage(url);
        } else {
            this.imgShow = this.imgDefault;
            this.checkImageValid(this.imgShow);
        }
    }

    next() {
        const max = (this.currentProduct?.resources?.length ?? 1) - 1;
        if (this.activeIndex < max) {
            this.activeIndex = this.activeIndex + 1;
            this.loadActiveImage();
            this.scrollToActiveThumbnail();
        }
    }

    prev() {
        if (this.activeIndex > 0) {
            this.activeIndex = this.activeIndex - 1;
            this.loadActiveImage();
            this.scrollToActiveThumbnail();
        }
    }

    /**
     * Scroll đến thumbnail đang active (nếu cần)
     */
    scrollToActiveThumbnail(): void {
        // Optional: scroll to active thumbnail nếu list dài
        setTimeout(() => {
            const activeElement = document.querySelector(`.thumbnail-item.active`) as HTMLElement;
            if (activeElement) {
                activeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }, 0);
    }

    /**
     * Kiểm tra xem LanguageProperty có giá trị thực sự hay không
     * Trả về false nếu field là null/undefined hoặc tất cả các giá trị đều rỗng
     */
    hasLanguagePropertyValue(field: any): boolean {
        if (!field) {
            return false;
        }

        // Nếu là string, kiểm tra có rỗng không
        if (typeof field === 'string') {
            return field.trim().length > 0;
        }

        // Nếu là object LanguageProperty, kiểm tra có ít nhất một giá trị không rỗng
        if (typeof field === 'object' && field !== null) {
            // Kiểm tra nếu là array (không phải LanguageProperty)
            if (Array.isArray(field)) {
                return false;
            }

            // Lấy tất cả các giá trị và kiểm tra
            const values = Object.values(field);
            if (values.length === 0) {
                return false;
            }

            // Kiểm tra có ít nhất một giá trị không rỗng
            const hasValue = values.some((value: any) => {
                if (value == null) {
                    return false;
                }
                if (typeof value === 'string') {
                    return value.trim().length > 0;
                }
                if (typeof value === 'number') {
                    return true; // Số 0 vẫn là giá trị hợp lệ
                }
                if (typeof value === 'boolean') {
                    return true; // Boolean luôn là giá trị hợp lệ
                }
                return false;
            });

            return hasValue;
        }

        return false;
    }

    /**
     * Kiểm tra xem có hiển thị Standards section hay không
     * Trả về true nếu có ít nhất một trong hai: CE hoặc Other Standards
     */
    hasStandardsValue(): boolean {
        if (!this.currentProduct) {
            return false;
        }

        const hasCE = this.currentProduct.ce === true;
        const hasOtherStandards = this.hasLanguagePropertyValue(this.currentProduct.otherStandards);

        return hasCE || hasOtherStandards;
    }

    /**
     * Format giá trị Standards để hiển thị: "CE / Other Standards"
     * Nếu một trong hai field rỗng, dấu "/" sẽ biến mất
     */
    getStandardsValue(): string {
        const hasCE = this.currentProduct?.ce === true;
        const hasOtherStandards = this.hasLanguagePropertyValue(
            this.currentProduct?.otherStandards
        );

        const parts: string[] = [];

        if (hasCE) {
            parts.push('CE');
        }

        if (hasOtherStandards) {
            const otherStandardsValue = this.translateDynamicLanguage.transform(
                this.currentProduct?.otherStandards
            );
            if (otherStandardsValue && otherStandardsValue.trim().length > 0) {
                parts.push(otherStandardsValue);
            }
        }

        return parts.join(' / ');
    }
}
